//Built-in libraries

//Third party libraries
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

//Custom components
import AttachmentContainer, { Attachment } from '../Attachments';

//Services
import {
  addApplicationFile,
  deleteApplicationFile,
  signApplicationPublicAsset,
} from 'api/Rover/ApplicationForm';
import { useFileUpload } from 'hooks/useFileUpload';
import { notifyUser } from 'store/Toast';

//STP Libraries

//Assets and Miscellaneous
import {
  IGetApplicationServiceResDataFile,
  ISignApplicationPublicAssetPayload,
} from 'api/Rover/ApplicationForm/types';
import { SignedApplicationAssetResponse } from 'types/Shared';
import { RoverReactQueryKey } from 'constants/ReactQueryKeys';
import { boardOfDirectorsMapping } from '../../LBAApplicationForm/LeadershipInformation/constant';

//Component Begin

interface ILeadershipInformationDetailsProps {
  leadershipInformation: any;
  files: IGetApplicationServiceResDataFile[];
}
export default function LeadershipInformationDetails({
  leadershipInformation,
  files,
}: ILeadershipInformationDetailsProps) {
  const { applicationFormId, applicationId } = useParams();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { upload } = useFileUpload({
    signAsset: signApplicationPublicAsset,
  });

  const leadershipFiles = files.filter(
    (file) => file.field === 'principalsResume'
  );

  const uploadFile = useMutation(
    (payload: {
      signAssetPayload: ISignApplicationPublicAssetPayload[];
      files: File[];
    }) => upload(payload),
    {
      onSuccess: (response: any) => {
        uploadApplicationFiles.mutate(response);
      },
      onError: (error: any) => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Failed to upload document`,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const uploadApplicationFiles = useMutation(
    (payload: {
      sign: SignedApplicationAssetResponse;
      files: File[];
      response: any;
    }) => {
      const { files, sign } = payload;
      return addApplicationFile({
        applicationId: String(applicationId),
        files: [
          {
            name: files[0].name,
            path: sign.path,
            key: sign.key,
            type: files[0].type,
            field: 'principalsResume',
            description: 'Principals Resume',
          },
        ],
      });
    },
    {
      onSuccess: (_, payload: { files: File[] }) => {
        queryClient.invalidateQueries([
          RoverReactQueryKey.GET_APPLICATION_DETAILS,
        ]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${payload.files[0].name} is successfully uploaded.`,
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Failed to upload document`,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const handleUploadDocument = (files: File[]) => {
    if (
      leadershipInformation.principalsResume?.findIndex(
        (resume: any) => resume.name === files[0].name
      ) !== -1
    ) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `File already exists.`,
          toastType: 'failed',
        })
      );
    } else {
      uploadFile.mutate({
        signAssetPayload: [
          {
            fileName: files[0].name,
            fileType: files[0].type,
            fileSize: files[0].size,
            formId: String(applicationFormId),
          },
        ],
        files,
      });
    }
  };

  const deleteFile = useMutation(
    (payload: { key: string }) =>
      deleteApplicationFile({
        ...payload,
        applicationId: String(applicationId),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          RoverReactQueryKey.GET_APPLICATION_DETAILS,
        ]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Successfully deleted file`,
            toastType: 'success',
          })
        );
      },
    }
  );

  const handleFileDelete = (key: string) => {
    deleteFile.mutate({ key });
  };

  return (
    <>
      <section aria-labelledby='applicant-information-title'>
        <div className='bg-white shadow sm:rounded-lg'>
          <div className='py-5'>
            <div className='px-4 py-2 sm:px-6 border-l-4 border-primaryColor-normal'>
              <h2
                id='applicant-information-title'
                className='m-0 text-lg leading-6 font-medium text-primaryColor-normal'
              >
                Leadership Information
              </h2>
              <p className='mt-1 max-w-2xl text-sm text-gray-500'>
                Leadership details and application.
              </p>
            </div>
          </div>
          <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
            <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 mb-5'>
              <div className='sm:col-span-2'>
                <dt className='text-sm font-medium text-gray-500'>
                  What racial/ethnic groups does the highest authority member of
                  management (President, CEO, Partner, etc) belong to?
                </dt>
                <dd className='mt-1 text-sm text-gray-900'>
                  {leadershipInformation.racialEthnicGroup.map(
                    (group: string) => (
                      <span key={group}>{group}, </span>
                    )
                  )}
                </dd>
              </div>
              <div className='sm:col-span-2'>
                <dt className='text-sm font-medium text-gray-500'>
                  Is the majority ownership (51% or greater) of the company held
                  by someone that is:
                </dt>
                <dd className='mt-1 text-sm text-gray-900'>
                  {leadershipInformation.majorityOwnership.map(
                    (major: string) => (
                      <span key={major}>{major}, </span>
                    )
                  )}
                </dd>
              </div>
              <div className='sm:col-span-2'>
                <dt className='text-sm font-medium text-gray-500'>
                  If the company is governed by a board of directors, please
                  specify the percentage of the board that identifies with any
                  of the groups below:
                </dt>
                <dd className='mt-1 text-sm text-gray-900'>
                  {Object.keys(
                    leadershipInformation.boardOfDirectorsPercent
                  ).map((key: string) => {
                    if (
                      leadershipInformation.boardOfDirectorsPercent[
                        key
                      ].trim() !== ''
                    ) {
                      return (
                        <span key={key}>
                          {boardOfDirectorsMapping[key]}(
                          {leadershipInformation.boardOfDirectorsPercent[key]}
                          %),{' '}
                        </span>
                      );
                    }
                  })}
                </dd>
              </div>
            </dl>
            <AttachmentContainer
              title='Principals Resume'
              uploadConfig={{
                onUpload: handleUploadDocument,
                isUploading: uploadFile.isLoading,
              }}
              isEmpty={!leadershipFiles || leadershipFiles?.length === 0}
            >
              {leadershipFiles?.map((doc) => (
                <Attachment
                  key={doc?.url}
                  title={doc?.name || ''}
                  doc={doc}
                  deleteFileHandler={handleFileDelete}
                  onClick={() => {
                    window.open(doc?.url || '');
                  }}
                />
              ))}
            </AttachmentContainer>
          </div>
        </div>
      </section>
    </>
  );
}
